import { FC, useCallback, useContext, useEffect, useState, type JSX } from "react";
import dynamic from "next/dynamic";
import dayjs from "dayjs";
import { Context } from "@/lib/Context";
import { useAtomValue } from "jotai";

import { Box } from "@mui/material";
import { Stock } from "@/models/stock";
import Module from "@/components/module/Module";
import TourGuide, { useTour } from "@/components/tour/TourGuide";
import { DataGrid } from "../../data-grid/DataGrid";
import { agreementsState, userState, whiteState } from "@/lib/store";
import { AgreeTermsMessage, VisitorsMessage } from "src/pages/options";
import { OptionsUpsellMessage } from "@/components/options-platform/offer-host/OptionsPlatformOfferHost";
import AgreementsForm from "@/components/agreements-form/AgreementsForm";
import { getStockOptionsInterestingFlow } from "@/controllers/stock-options";
import StockOptionsITM from "@/components/stock-options-itm/StockOptionsITM";
import StockOptionsChain from "@/components/stock-options-chain/StockOptionsChain";
import StockOptionsSentiment from "@/components/stock-options-sentiment/StockOptionsSentiment";

import styles from "./StockOptionsOverview.module.scss";

const PutCallRatioChart = dynamic(() => import("@/components/put-call-ratio-chart/PutCallRatioChart"), { ssr: false });

const StockOptionsOverview: FC<{ stock: Stock }> = ({ stock }) => {
    const user = useAtomValue(userState);
    // On-demand tour — launch from the Put/Call Ratio module's ⓘ Help.
    const tour = useTour("options_stock_overview", false);

    const { ortexWebSocket } = useContext(Context);

    const white = useAtomValue(whiteState);
    const agreements = useAtomValue(agreementsState);

    const [form, setForm] = useState(false);
    const [row, setRow] = useState<any[]>([]);
    const [optionsFlowFilters, setOptionsFlowFilters] = useState<JSX.Element>();

    const sendMessage = useCallback(() => {
        ortexWebSocket.send(JSON.stringify({ type: "subscribe", identifier: "#OIT" }));
    }, [agreements, ortexWebSocket]);

    const onMessage = useCallback(
        ({ data }: MessageEvent) => {
            const { "#OIT": OIT } = JSON.parse(data);
            if (OIT?.UL === stock.ticker)
                setRow([
                    {
                        time: dayjs.utc(OIT.T).valueOf(),
                        expiryDate: dayjs.utc(OIT.EXP, "YYMMDD").valueOf(),
                        strike: OIT.strike,
                        size: OIT.total_volume,
                        callPut: OIT.OT,
                        price: OIT.avg_price,
                        reason: OIT.trigger_reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
                        id: OIT.uuid,
                        totalValue: OIT.total_value,
                    },
                ]);
        },
        [stock]
    );

    useEffect(() => {
        if (agreements?.find(({ code }) => code === "OPRA") && ortexWebSocket) {
            ortexWebSocket.readyState === 1 ? sendMessage() : ortexWebSocket.addEventListener("open", sendMessage);
            ortexWebSocket.addEventListener("message", onMessage);
        }

        return () => {
            if (agreements?.find(({ code }) => code === "OPRA") && ortexWebSocket) {
                ortexWebSocket.removeEventListener("open", sendMessage);
                ortexWebSocket.removeEventListener("message", onMessage);
                if (ortexWebSocket.readyState === 1) ortexWebSocket.send(JSON.stringify({ type: "unsubscribe", identifier: "#OIT" }));
            }
        };
    }, [agreements, ortexWebSocket, sendMessage, onMessage]);

    return (
        <Box className={styles.StockOptions}>
            {agreements?.find(({ code }) => code === "OPRA") ? (
                <div className={styles.data}>
                    <div className={styles.chain} data-tour="overview-chain">
                        <StockOptionsChain stock={stock} />
                    </div>
                    <div className={styles.sentiment} data-tour="overview-sentiment">
                        <StockOptionsSentiment stock={stock} />
                    </div>
                    <div className={styles.chart} data-tour="overview-pcr">
                        <Module title="Put/Call Ratio" help={{ moduleId: "put_call_ratio_chart", modulePage: "V2_stock_options" }} helpActions={[{ label: "Restart the guided tour", onClick: tour.start }]} allowFullscreen feedback>
                            <PutCallRatioChart stock={stock} borderRadius />
                        </Module>
                    </div>
                    <div className={styles.flow} data-tour="overview-flow">
                        <Module title={`${stock.name} Interesting Options Flow`} help={{ moduleId: "options_flow", modulePage: "V2_stock_options" }} allowFullscreen feedback filters={optionsFlowFilters}>
                            <DataGrid id={stock.tradingitem_id} fetcher={getStockOptionsInterestingFlow} extraRow={row} setFilters={setOptionsFlowFilters} tableId="stock-options-flow" forwardLookingDateFields={["expiryDate"]} />
                        </Module>
                    </div>
                    <div className={styles["in-the-money"]} data-tour="overview-itm">
                        <StockOptionsITM stock={stock} />
                    </div>
                </div>
            ) : (
                <div className={styles["opra-form"]}>
                    <Module>{user.subscription_plan === "ORTEX Visitor" ? <VisitorsMessage color={white} /> : user.options_legacy ? <AgreeTermsMessage color={white} setForm={setForm} /> : <OptionsUpsellMessage color={white} />}</Module>
                </div>
            )}
            {form && <AgreementsForm isOpen={form} onClose={setForm} />}
            <TourGuide {...tour.props} />
        </Box>
    );
};

export default StockOptionsOverview;
